/* eslint-disable @typescript-eslint/no-explicit-any */
import { OptionsFieldOption, QuestionnaireComponentKind } from '@main/dynamic-form';

import { QuestionConditionChecker } from '../lib/conditions';
import { QuestionnaireFormFieldConfig, QuestionnnaireFormFieldAnswer } from '../lib/form-types';
import { getQuestionConditionsFilter } from './conditions';

export function getFormConfigFilter(
  answers: readonly QuestionnnaireFormFieldAnswer[],
  answersMap: ReadonlyMap<string, QuestionnnaireFormFieldAnswer>,
  conditionsFiltersMap: ReadonlyMap<string, QuestionConditionChecker>,
) {
  return (field: QuestionnaireFormFieldConfig) =>
    conditionsFiltersMap.get(field.name)?.(answers, answersMap.get(field.name)) !== true;
}

export function getFilteredConfig(
  config: readonly QuestionnaireFormFieldConfig[],
  answers: readonly QuestionnnaireFormFieldAnswer[],
  answersMap = new Map(answers.map((answer) => [answer.field_name, answer])),
): QuestionnaireFormFieldConfig[] {
  const conditionsFiltersMap = new Map(
    config.map((field) => [field.name, getQuestionConditionsFilter(field, config)]),
  );

  return config
    .filter(getFormConfigFilter(answers, answersMap, conditionsFiltersMap))
    .map((field) => {
      if (field.kind === QuestionnaireComponentKind.Section && field.fields) {
        return {
          ...field,
          fields: getFilteredConfig(field.fields, answers, answersMap),
        };
      }

      return field;
    });
}

export type FlatQuestionnaireFormFieldConfig = readonly QuestionnaireFormFieldConfig[] & {
  _flatConfigMarker: 'Use flattenFields() to get flat fields';
};

export function flattenFields(
  fields: readonly QuestionnaireFormFieldConfig[],
): FlatQuestionnaireFormFieldConfig {
  return fields.flatMap((field) => [
    field,
    ...(field.kind === QuestionnaireComponentKind.Section && field.fields
      ? flattenFields(field.fields)
      : []),
  ]) as unknown as FlatQuestionnaireFormFieldConfig;
}

export function mapField<R extends QuestionnaireFormFieldConfig, TDeps extends any[]>(
  mapFn: (field: QuestionnaireFormFieldConfig, ...deps: TDeps) => R,
  field: QuestionnaireFormFieldConfig,
  ...deps: TDeps
): R {
  if (field.kind === QuestionnaireComponentKind.Section && field.fields) {
    field = { ...field, fields: mapFields(mapFn, field.fields, ...deps) };
  }

  return mapFn(field, ...deps);
}

export function mapFieldPartial<R extends QuestionnaireFormFieldConfig, TDeps extends any[]>(
  mapFn: (field: QuestionnaireFormFieldConfig, ...deps: TDeps) => R,
) {
  return (field: QuestionnaireFormFieldConfig, ...deps: TDeps) => mapField(mapFn, field, ...deps);
}

export function mapFields<R extends QuestionnaireFormFieldConfig, TDeps extends any[]>(
  mapFn: (field: QuestionnaireFormFieldConfig, ...deps: TDeps) => R,
  fields: readonly QuestionnaireFormFieldConfig[],
  ...deps: TDeps
): R[] {
  return fields.map((field) => mapField(mapFn, field, ...deps));
}

export function mapFieldsPartial<R extends QuestionnaireFormFieldConfig, TDeps extends any[]>(
  mapFn: (field: QuestionnaireFormFieldConfig, ...deps: TDeps) => R,
) {
  return (fields: readonly QuestionnaireFormFieldConfig[], ...deps: TDeps) =>
    mapFields(mapFn, fields, ...deps);
}

export function mapFieldOptions<TDeps extends any[]>(
  mapFn: (option: OptionsFieldOption, ...deps: TDeps) => OptionsFieldOption,
  field: QuestionnaireFormFieldConfig,
  ...deps: TDeps
): QuestionnaireFormFieldConfig {
  if (field.kind !== QuestionnaireComponentKind.Options) {
    return field;
  }

  return { ...field, options: field.options?.map((option) => mapFn(option, ...deps)) };
}

export function mapFieldOptionsPartial<TDeps extends any[]>(
  mapFn: (option: OptionsFieldOption, ...deps: TDeps) => OptionsFieldOption,
) {
  return (field: QuestionnaireFormFieldConfig, ...deps: TDeps) =>
    mapFieldOptions(mapFn, field, ...deps);
}
